import React, { useEffect, useState, useRef } from 'react';
import { Container, Typography, Box, Paper } from '@mui/material';
import ChamadoSocorristaList from './chamadoSocorristaList';
import { useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';

const PartnerEmergencies: React.FC = () => {
  const [aceitos, setAceitos] = useState(0);
  const navigate = useNavigate();
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const previousChamadosRef = useRef<string[]>([]);
  const hasCheckedInitialRef = useRef(false);

  // Função para obter valor do storage (mobile ou web)
  const getStorageValue = async (key: string): Promise<string | null> => {
    if (Capacitor.isNativePlatform()) {
      const { value } = await Preferences.get({ key });
      return value;
    } else {
      return localStorage.getItem(key);
    }
  };

  // Função para salvar valor no storage (mobile ou web)
  const setStorageValue = async (key: string, value: string): Promise<void> => {
    if (Capacitor.isNativePlatform()) {
      await Preferences.set({ key, value });
    } else {
      localStorage.setItem(key, value);
    }
  };

  // Função para ativar o sino no Navbar
  const ativarSinoNotificacao = () => {
    const event = new CustomEvent('novaNotificacao');
    window.dispatchEvent(event);
  };

  // Função para tocar som de notificação
  const tocarSomNotificacao = () => {
    if (audioRef.current) {
      audioRef.current.play().catch(err => {
        console.log('Áudio bloqueado pelo navegador - requer interação do usuário');
      });
    }
  };

  useEffect(() => {
    const verificarChamadoAberto = async () => {
      try {
        const url = process.env.REACT_APP_SUPABASE_URL;
        const serviceKey = process.env.REACT_APP_SUPABASE_SERVICE_KEY;
        const accessToken = await getStorageValue('accessToken') || await getStorageValue('userToken');
        const socorristaId = await getStorageValue('socorristaId');
        
        if (!url || !serviceKey || !socorristaId || !accessToken) {
          return;
        }

        // Construir data de hoje
        const today = new Date();
        const yyyy = today.getFullYear();
        const mm = String(today.getMonth() + 1).padStart(2, '0');
        const dd = String(today.getDate()).padStart(2, '0');
        const todayStr = `${yyyy}-${mm}-${dd}`;
        
        // Buscar chamados "A caminho" de hoje
        const endpoint = `${url}/rest/v1/chamado?socorrista_id=eq.${socorristaId}&status=eq.A caminho&data_abertura=gte.${todayStr}T00:00:00&data_abertura=lte.${todayStr}T23:59:59&order=data_abertura.desc`;
        
        const response = await fetch(endpoint, {
          headers: {
            'apikey': serviceKey,
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          return;
        }

        const data = await response.json();
        
        if (Array.isArray(data) && data.length > 0) {
          // Obter IDs dos chamados atuais
          const currentIds = data.map((c: any) => c.id.toString());
          const previousIds = previousChamadosRef.current;
          
          // Verificar se há novos chamados (somente após a primeira verificação)
          if (hasCheckedInitialRef.current && previousIds.length > 0) {
            const newChamados = currentIds.filter((id: string) => !previousIds.includes(id));
            if (newChamados.length > 0) {
              console.log('Novos chamados detectados:', newChamados);
              ativarSinoNotificacao();
              tocarSomNotificacao();
            }
          }
          
          // Atualizar a referência dos IDs anteriores
          previousChamadosRef.current = currentIds;
          
          // Navegar apenas para o primeiro chamado
          const chamado = data[0];
          await setStorageValue('chamadoId', chamado.id.toString());
          
          // Marcar que já fizemos a verificação inicial
          if (!hasCheckedInitialRef.current) {
            hasCheckedInitialRef.current = true;
          }
          
          navigate('/emergencia-socorrista');
        } else {
          // Se não há chamados, limpar a referência
          previousChamadosRef.current = [];
        }
        
      } catch (err) {
        console.error('Erro ao verificar chamados:', err);
      }
    };

    verificarChamadoAberto();
    
    // Configurar intervalo para verificar periodicamente (opcional)
    const interval = setInterval(verificarChamadoAberto, 30000); // Verifica a cada 30 segundos
    
    return () => clearInterval(interval);
  }, [navigate]);

  useEffect(() => {
    const fetchAceitos = async () => {
      try {
        const url = process.env.REACT_APP_SUPABASE_URL;
        const serviceKey = process.env.REACT_APP_SUPABASE_SERVICE_KEY;
        const accessToken = await getStorageValue('accessToken') || await getStorageValue('userToken');
        const socorristaId = await getStorageValue('socorristaId');
        
        if (!url || !serviceKey || !socorristaId || !accessToken) {
          return;
        }

        // Contar todos os chamados
        const apiUrl = `${url}/rest/v1/chamado?socorrista_id=eq.${socorristaId}`;
        
        const response = await fetch(apiUrl, {
          method: 'GET',
          headers: {
            'apikey': serviceKey,
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (!response.ok) {
          return;
        }

        const data = await response.json();
        
        if (Array.isArray(data)) {
          setAceitos(data.length);
        }
        
      } catch (error) {
        console.error('Erro ao buscar aceitos:', error);
      }
    };

    fetchAceitos();
  }, []);

  return (
    <>
      {/* Elemento de áudio para notificações */}
      <audio ref={audioRef} src={`${process.env.PUBLIC_URL}/assets/notification.mp3`} />
      
      <Container
        maxWidth="lg"
        sx={{
          py: 4,
          px: 0,
          mx: 'auto',
          position: 'relative',
          overflow: 'hidden',
          minHeight: 'calc(100vh - 64px)'
        }}
      >
        {/* Marca d'água */}
        <Box
          component="img"
          src={require('../../assets/Design sem nome (7).png')}
          alt="Marca d'água Support Life"
          sx={{
            position: 'absolute',
            top: '30%',
            left: '50%',
            width: '180%',
            height: '180%',
            objectFit: 'contain',
            opacity: 0.07,
            pointerEvents: 'none',
            zIndex: 0,
            transform: 'translate(-50%, -50%)',
          }}
        />
        
        {/* Conteúdo principal */}
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Typography variant="h4" gutterBottom>
            Chamados de Emergência
          </Typography>
          
          <Paper elevation={0} sx={{ 
            mb: 4, 
            p: 3, 
            textAlign: 'center',
            backgroundColor: 'transparent', 
            boxShadow: 'none', 
            border: 'none' 
          }}>
            <Typography variant="h6">Chamados aceitos pelo socorrista</Typography>
            <Typography variant="h3" color="primary">{aceitos}</Typography>
          </Paper>
          
          <ChamadoSocorristaList />
        </Box>
      </Container>
    </>
  );
};

export default PartnerEmergencies;